import { normalizeDollarAmount } from '@/helpers/normalizeDollarAmount';
import { useState } from 'react';
import { useMemo } from 'react';
import type { PayoutRequest } from '@skillscratch/shared-types';
import { callFirebaseFunction } from '@/bridge/callFirebaseFunction';
import { getAuth } from 'firebase/auth';
import { CircularLoadingSpinner } from './CircularLoadingSpinner/CircularLoadingSpinner';
import { centsToPrettyDollars } from '@skillscratch/shared-util';
import { useFirebaseFunction } from '@/hooks/useFirebaseFunction';
import paypal from "@/assets/payment-logos/paypal.svg";
import venmo from "@/assets/payment-logos/venmo.svg";
import bank from "@/assets/bank.svg";
import { useEffect } from 'react';


type GetUserPrivateInfoRequest = {
    fields?: string[];
  };
  
  type GetUserPrivateInfoResponse = {
    userInfo: {
      lifetimeWithdrawn?: number;
    };
  };

export function InitiateWithdrawalSection() {
    const [amount, setAmount] = useState('');
    const [error, setError] = useState('');
    const [warning, setWarning] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [success, setSuccess] = useState('');
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null);
    const [paymentCredentials, setPaymentCredentials] = useState<string>('');
    const [lifetimeWithdrawnResponse, isWithdrawalLoading, ] = useFirebaseFunction<GetUserPrivateInfoRequest, GetUserPrivateInfoResponse>(
        "getUserPrivateInfo",
        (fn) => fn({ fields: ['lifetimeWithdrawn'] }),
        []
    );

    const auth = getAuth();

    const fee = useMemo(() => {
        return Math.max(1, Number.parseInt(amount) * 0.02)
    }, [amount]);

    const cantInitiate = useMemo(() => {
        return !amount || !!warning || !!error;   
    }, [amount, error, warning]);
    
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    const handleAmountChange = (e: { target: { value: any; }; }) => {
        const value = e.target.value;
        
        // Allow any input, but check for non-numeric characters
        setAmount(value);
        
        // Clear warnings first
        setWarning('');
        
        // Clear success message when input changes
        setSuccess('');
        
        // Check for non-numeric characters (except decimal point)
        if (value !== '' && !/^[\d.]+$/.test(value)) {
            setWarning('Only numbers allowed');
        }
        
        // Check for multiple decimal points
        if ((value.match(/\./g) || []).length > 1) {
            setWarning('Only one decimal point allowed');
        }
        
        // Validate minimum amount if value is a valid number
        if (value && !Number.isNaN(Number.parseFloat(value))) {
            if (Number.parseFloat(value) < 10) {
                setError('Minimum $10 withdrawal');
            } else {
                setError('');
            }
        } else {
            setError('');
        }
    };

    useEffect(() => {
        if (selectedPaymentMethod && error === 'Please select payment method') {
            setError('');
        }

        if (paymentCredentials && error === 'Please add payment credentials') {
            setError('');
        }
    }, [selectedPaymentMethod, paymentCredentials, error])
    
    const handleInitiate = () => {
        if (!amount) {
            setError('Please enter an amount');
            return;
        }
        
        const numAmount = Number.parseFloat(amount);
        if (Number.isNaN(numAmount)) {
            setError('Please enter a valid amount');
            return;
        }
        
        if (numAmount < 10) {
            setError('Amount must be at least $10');
            return;
        }

        if (!selectedPaymentMethod) {
            setError('Please select payment method');
            return;
        }

        if (!paymentCredentials) {
            setError('Please add payment credentials');
            return;
        }

        // Log the payment credentials for debugging
        console.log('Selected Payment Method:', selectedPaymentMethod);
        console.log('Payment Credentials:', paymentCredentials);

        setIsLoading(true);
        setSuccess('');
        setError('');
        const normalizedPayoutAmount = normalizeDollarAmount(amount);

        if (!normalizedPayoutAmount) {
            throw new Error('Invalid payout amount');
        }

        callFirebaseFunction<PayoutRequest, string>("manualPayout", {
            amount: normalizedPayoutAmount,
            phone: auth.currentUser?.phoneNumber ?? '',
            manual: true,
            method: selectedPaymentMethod as PayoutRequest['method'] ?? undefined,
            credentials: paymentCredentials,
            uid: auth.currentUser?.uid ?? 'Error determining uid',
          })
            .then(() => {
              setSuccess(`Withdrawal of ${centsToPrettyDollars(normalizeDollarAmount(amount) as number - 100)} successful!`);
              setAmount(''); // Clear the input field after successful submission
            })
            .catch((error) => {
              setError(`${error.message}: Please make sure you are not using a VOIP number.`);
              // should probably show an error/handle with sentry/post
            })
            .finally(() => {
              setIsLoading(false);
            });

        
        // Process withdrawal
        // console.log(`Initiating withdrawal of ${amount}`);
    };

    return (
        <div className='mb-8'>
            <h2 className="text-2xl font-bold text-blue-dark mb-1">Initiate a withdrawal</h2>
            
            <div className="mb-6 flex flex-col space-y-3">
                <p className="text-blue-dark text-sm">
                    Start a withdrawal with 13+ secure methods. 
                    $10 minimum with a 2% required payouts fee ($1 minimum fee).
                </p>
                <p className="text-blue-dark text-sm">
                    Fees are charged by our partners. We recommend withdrawing infrequently, like after large wins or when funds are needed.
                </p>
            </div>

            <div className="my-6">
                <PaymentOptions 
                    selectedPaymentMethod={selectedPaymentMethod}
                    setSelectedPaymentMethod={setSelectedPaymentMethod}
                    paymentCredentials={paymentCredentials}
                    setPaymentCredentials={setPaymentCredentials}
                />
            </div>

            <div className="flex flex-col space-y-4">
                <div className="flex gap-2">
                    <div className="relative flex-grow">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-dark">$</span>
                        <input 
                            type="text" 
                            className="border border-gray-200 text-blue-dark rounded-lg p-2 pl-6 w-full" 
                            placeholder="Type here, eg $100"
                            value={amount}
                            onChange={handleAmountChange}
                            aria-invalid={!!error || !!warning}
                        />
                    </div>
                    {/* biome-ignore lint/a11y/useButtonType: <explanation> */}
                    <button 
                        className="bg-gray-200 hover:bg-gray-300 rounded-lg px-4 py-2 text-blue-dark disabled:opacity-50"
                        onClick={handleInitiate}
                        disabled={cantInitiate}
                    >
                        { isLoading ? <CircularLoadingSpinner /> : <span>Initiate</span> }
                    </button>
                </div>
                
                {warning && (
                    <div className="bg-yellow-50 border-yellow-200 text-yellow-600 border rounded-lg py-3 px-1 text-center font-semibold">
                        {warning}
                    </div>
                )}
                    
                {error && (
                    <div className='bg-red-50 border-red-200 text-red-600 border rounded-lg p-3 text-center font-semibold'>
                        {error}
                    </div>
                )}

                {success && (
                    <div className='flex flex-col gap-2'>
                        <div className='bg-green-50 border-green-200 text-green-600 border rounded-lg p-3 text-center font-semibold'>
                            {success}
                        </div>
                        <GetSuccessInfo lifetimeWithdrawn={lifetimeWithdrawnResponse?.userInfo.lifetimeWithdrawn ?? 0} isLoading={isWithdrawalLoading} />
                    </div>
                )}

                { 
                    !cantInitiate && (
                        <div className="bg-yellow-50 border-yellow-200 text-yellow-600 border rounded-lg py-3 px-1 text-center font-semibold">
                            ${fee} fee. Minimize payouts. <br />
                        </div>
                    )
                }
            </div>
        </div>
    );
}

export function GetSuccessInfo({ lifetimeWithdrawn, isLoading }: { lifetimeWithdrawn: number, isLoading: boolean }) {
    console.log('[lifetimeWithdrawn]', lifetimeWithdrawn);

    if (isLoading) {
        return (
            <div className='bg-gray-50 border-gray-200 text-gray-600 border rounded-lg p-3 text-center font-semibold'>
                <CircularLoadingSpinner />
            </div>
        )
    }

    if (lifetimeWithdrawn > 0) {
        return (
            <div className='bg-gray-50 border-gray-200 text-gray-600 border rounded-lg p-3 text-center font-semibold'>
                It'll arrive to your default method within ~15 minutes
            </div>
        )
    }

    return (
        <div className='bg-gray-50 border-gray-200 text-gray-600 border rounded-lg p-3 text-center font-semibold'>
            Check your texts to pick a payment method
        </div>
    )
}

function PaymentOptions({ 
    selectedPaymentMethod, 
    setSelectedPaymentMethod, 
    paymentCredentials, 
    setPaymentCredentials 
}: {
    selectedPaymentMethod: string | null;
    setSelectedPaymentMethod: (method: string | null) => void;
    paymentCredentials: string;
    setPaymentCredentials: (credentials: string) => void;
}) {
    const options = [
      { id: 'paypal', icon: paypal, alt: 'PayPal' },
      { id: 'venmo', icon: venmo, alt: 'Venmo' },
      { id: 'bank', icon: bank, alt: 'Bank', label: 'Bank' }
    ];

    const getPaymentMethodInfo = (optionId: string) => {
      switch (optionId) {
        case 'paypal':
          return <PayPalCredentials 
            credentials={paymentCredentials} 
            setCredentials={setPaymentCredentials} 
          />;
        case 'venmo':
          return <VenmoCredentials 
            credentials={paymentCredentials} 
            setCredentials={setPaymentCredentials} 
          />;
        case 'bank':
          return <BankCredentials 
            credentials={paymentCredentials} 
            setCredentials={setPaymentCredentials} 
          />;
        default:
          return null;
      }
    };

    const handleOptionClick = (optionId: string) => {
        if (selectedPaymentMethod === optionId) {
            setSelectedPaymentMethod(null);
            setPaymentCredentials('');
        } else {
            setSelectedPaymentMethod(optionId);
            setPaymentCredentials(''); // Clear credentials when switching
        }
    };
  
    return (
        <div className="space-y-3">
            <div className="flex items-center w-full justify-between gap-2">
                {options.map((option) => (
                // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
                <div
                    key={option.id}
                    onClick={() => handleOptionClick(option.id)}
                    className={`
                    p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 
                    hover:shadow-md flex-1 flex justify-center
                    ${selectedPaymentMethod === option.id 
                        ? 'border-blue bg-blue-50 shadow-md' 
                        : 'border-gray-200 hover:border-gray-300'
                    }
                    `}
                >
                    {option.id === 'bank' ? (
                    <span className='flex items-center space-x-1.5'>
                        <img src={option.icon} alt={option.alt} className='h-5' />
                        <span className='text-md font-medium text-blue-dark'>
                        {option.label}
                        </span>
                    </span>
                    ) : (
                    <img src={option.icon} alt={option.alt} className="h-5" />
                    )}
                </div>
                ))}
            </div>
            
            {/* Accordion content that slides down */}
            <div 
                className={`
                    transition-all duration-300 ease-in-out overflow-hidden
                    ${selectedPaymentMethod 
                        ? 'max-h-96 opacity-100 mt-4' 
                        : 'max-h-0 opacity-0 mt-0'
                    }
                `}
            >
                <div 
                    key={selectedPaymentMethod}
                    className="transition-all duration-200 ease-in-out"
                    style={{
                        animation: selectedPaymentMethod ? 'fadeInSlide 200ms ease-in-out' : 'none'
                    }}
                >
                    {selectedPaymentMethod && getPaymentMethodInfo(selectedPaymentMethod)}
                </div>
            </div>
            
            <style>{`
                @keyframes fadeInSlide {
                    0% {
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                    100% {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>
        </div>
    );
}

function PayPalCredentials({ credentials, setCredentials }: { credentials: string; setCredentials: (value: string) => void }) {
    return (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-dark text-sm mb-3">PayPal Information</h4>
            <input 
                type="email" 
                className="border border-gray-200 text-blue-dark rounded-lg p-3 w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                placeholder="Enter your PayPal email address"
                value={credentials}
                onChange={(e) => setCredentials(e.target.value)}
            />
        </div>
    );
}

function VenmoCredentials({ credentials, setCredentials }: { credentials: string; setCredentials: (value: string) => void }) {
    return (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-dark text-sm mb-3">Venmo Information</h4>
            <input 
                type="text" 
                className="border border-gray-200 text-blue-dark rounded-lg p-3 w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                placeholder="Enter your Venmo username"
                value={credentials}
                onChange={(e) => setCredentials(e.target.value)}
            />
        </div>
    );
}

function BankCredentials({ credentials, setCredentials }: { credentials: string; setCredentials: (value: string) => void }) {
    // Parse existing credentials
    const parsedCreds = credentials.startsWith('Routing number: ') 
        ? credentials.replace('Routing number: ', '').split(', Account number: ')
        : ['', ''];
    
    const [routingNumber, accountNumber] = parsedCreds;

    const updateCredentials = (routing: string, account: string) => {
        setCredentials(`Routing number: ${routing}, Account number: ${account}`);
    };

    return (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-dark text-sm mb-3">Bank Wire Information</h4>
            <div className="space-y-3">
                <input 
                    type="text" 
                    className="border border-gray-200 text-blue-dark rounded-lg p-3 w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                    placeholder="Routing number (9 digits)"
                    value={routingNumber}
                    onChange={(e) => updateCredentials(e.target.value, accountNumber)}
                    maxLength={9}
                />
                <input 
                    type="text" 
                    className="border border-gray-200 text-blue-dark rounded-lg p-3 w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                    placeholder="Account number"
                    value={accountNumber}
                    onChange={(e) => updateCredentials(routingNumber, e.target.value)}
                />
            </div>
        </div>
    );
}